import { httpClient } from '@/api/httpClient';
import {
  mapPaginatedResource,
  type PaginatedApiResource,
  type PaginatedResult,
} from '@/api/pagination.types';
import {
  mapApiResourceToSupportAttachmentInit,
  mapApiResourceToSupportMessage,
  mapApiResourceToSupportTicket,
  mapApiResourceToSupportTicketDetail,
} from './supportMapping';
import {
  type SupportAttachmentInit,
  type SupportAttachmentInitApiResource,
  type SupportAttachmentInitPayload,
  type SupportMessage,
  type SupportMessageApiResource,
  type SupportMessageCursor,
  type SupportScope,
  type SupportTicket,
  type SupportTicketApiResource,
  type SupportTicketCreatePayload,
  type SupportTicketDetail,
  type SupportTicketDetailApiResource,
  type SupportTicketFilters,
  type SupportUnreadCountApiResource,
} from './supportApi.types';

function resolveBasePath(scope: SupportScope): string {
  return scope === 'practitioner' ? '/support' : '/platform/support';
}

function buildListParams(page: number, filters: SupportTicketFilters): Record<string, unknown> {
  const trimmedSearch = filters.search.trim();
  const params: Record<string, unknown> = { page };
  if (trimmedSearch.length > 0) {
    params.search = trimmedSearch;
  }
  if (filters.status !== null) {
    params.status_filter = filters.status;
  }
  if (filters.assigneeUserId !== null) {
    params.assignee_user_id = filters.assigneeUserId;
  }
  if (filters.unassigned === true) {
    params.unassigned = true;
  }
  return params;
}

export async function createSupportTicket(
  payload: SupportTicketCreatePayload
): Promise<SupportTicketDetail> {
  const response = await httpClient.post<SupportTicketDetailApiResource>('/support/tickets', {
    subject: payload.subject,
    category: payload.category,
    priority: payload.priority,
    body: payload.body,
    attachment_ids: payload.attachmentIds,
  });
  return mapApiResourceToSupportTicketDetail(response.data);
}

export async function fetchSupportTickets(
  scope: SupportScope,
  page: number,
  filters: SupportTicketFilters
): Promise<PaginatedResult<SupportTicket>> {
  const response = await httpClient.get<PaginatedApiResource<SupportTicketApiResource>>(
    resolveBasePath(scope) + '/tickets',
    { params: buildListParams(page, filters) }
  );
  return mapPaginatedResource(response.data, mapApiResourceToSupportTicket);
}

export async function fetchSupportTicketDetail(
  scope: SupportScope,
  ticketId: string
): Promise<SupportTicketDetail> {
  const response = await httpClient.get<SupportTicketDetailApiResource>(
    resolveBasePath(scope) + '/tickets/' + ticketId
  );
  return mapApiResourceToSupportTicketDetail(response.data);
}

export async function fetchSupportTicketMessages(
  scope: SupportScope,
  ticketId: string,
  cursor: SupportMessageCursor
): Promise<SupportMessage[]> {
  const response = await httpClient.get<SupportMessageApiResource[]>(
    resolveBasePath(scope) + '/tickets/' + ticketId + '/messages',
    { params: { before: cursor.beforeId, after: cursor.afterId } }
  );
  return response.data.map(mapApiResourceToSupportMessage);
}

export async function sendSupportMessage(
  scope: SupportScope,
  ticketId: string,
  body: string | null,
  attachmentIds: string[]
): Promise<SupportMessage> {
  const response = await httpClient.post<SupportMessageApiResource>(
    resolveBasePath(scope) + '/tickets/' + ticketId + '/messages',
    { body, attachment_ids: attachmentIds }
  );
  return mapApiResourceToSupportMessage(response.data);
}

export async function markSupportTicketRead(scope: SupportScope, ticketId: string): Promise<void> {
  await httpClient.post(resolveBasePath(scope) + '/tickets/' + ticketId + '/read');
}

export async function initSupportTicketDraftAttachment(
  payload: SupportAttachmentInitPayload
): Promise<SupportAttachmentInit> {
  const response = await httpClient.post<SupportAttachmentInitApiResource>('/support/attachments', {
    filename: payload.filename,
    content_type: payload.contentType,
    size_bytes: payload.sizeBytes,
  });
  return mapApiResourceToSupportAttachmentInit(response.data);
}

export async function initSupportMessageAttachment(
  scope: SupportScope,
  ticketId: string,
  payload: SupportAttachmentInitPayload
): Promise<SupportAttachmentInit> {
  const response = await httpClient.post<SupportAttachmentInitApiResource>(
    resolveBasePath(scope) + '/tickets/' + ticketId + '/attachments',
    {
      filename: payload.filename,
      content_type: payload.contentType,
      size_bytes: payload.sizeBytes,
    }
  );
  return mapApiResourceToSupportAttachmentInit(response.data);
}

export async function fetchSupportAttachmentUrl(
  scope: SupportScope,
  attachmentId: string,
  disposition: 'inline' | 'attachment' = 'inline'
): Promise<string> {
  const response = await httpClient.get<{ download_url: string }>(
    resolveBasePath(scope) + '/attachments/' + attachmentId + '/download-url',
    { params: { disposition } }
  );
  return response.data.download_url;
}

export async function assignSupportTicket(
  ticketId: string,
  assigneeUserId: string
): Promise<SupportTicketDetail> {
  const response = await httpClient.post<SupportTicketDetailApiResource>(
    '/platform/support/tickets/' + ticketId + '/assign',
    { assignee_user_id: assigneeUserId }
  );
  return mapApiResourceToSupportTicketDetail(response.data);
}

export async function changeSupportTicketStatus(
  ticketId: string,
  ticketStatus: string,
  note: string | null
): Promise<SupportTicketDetail> {
  const response = await httpClient.post<SupportTicketDetailApiResource>(
    '/platform/support/tickets/' + ticketId + '/status',
    { status: ticketStatus, note }
  );
  return mapApiResourceToSupportTicketDetail(response.data);
}

export async function changeSupportTicketPriority(
  ticketId: string,
  priority: string
): Promise<SupportTicketDetail> {
  const response = await httpClient.post<SupportTicketDetailApiResource>(
    '/platform/support/tickets/' + ticketId + '/priority',
    { priority }
  );
  return mapApiResourceToSupportTicketDetail(response.data);
}

export async function reopenSupportTicket(ticketId: string): Promise<SupportTicketDetail> {
  const response = await httpClient.post<SupportTicketDetailApiResource>(
    '/support/tickets/' + ticketId + '/reopen'
  );
  return mapApiResourceToSupportTicketDetail(response.data);
}

export async function closeSupportTicket(ticketId: string): Promise<SupportTicketDetail> {
  const response = await httpClient.post<SupportTicketDetailApiResource>(
    '/support/tickets/' + ticketId + '/close'
  );
  return mapApiResourceToSupportTicketDetail(response.data);
}

export async function fetchSupportUnreadCount(scope: SupportScope): Promise<number> {
  const response = await httpClient.get<SupportUnreadCountApiResource>(
    resolveBasePath(scope) + '/unread-count'
  );
  return response.data.unread;
}
